/**
 * Storage utility for task filters
 *
 * This provides tab-specific storage for task filters using sessionStorage,
 * which is isolated to the current tab/window. This allows different tabs
 * to have different filter settings.
 */

import { GetTasksQueryParamsV4 } from "@fartherfinance/frontend/api/TaskWorkflows/requests/getTasks";

import { taskFiltersStorageKey } from "@src/constants/localStorageKeys";

const tabKey = `${taskFiltersStorageKey}.tab`;

/**
 * Saves task filters to tab-specific storage (sessionStorage)
 */
export function saveFiltersToTab(filters: GetTasksQueryParamsV4): void {
  try {
    sessionStorage.setItem(tabKey, JSON.stringify(filters));
  } catch (error) {
    console.error(
      `Error saving task filters to session storage under key (${tabKey}):`,
      error
    );
  }
}

/**
 * Gets task filters from tab-specific storage (sessionStorage)
 */
export function getFiltersFromTab(
  defaultValue: GetTasksQueryParamsV4
): GetTasksQueryParamsV4 {
  try {
    const savedFilters = sessionStorage.getItem(tabKey);

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      // Basic validation
      if (parsedFilters && typeof parsedFilters === "object") {
        return parsedFilters as GetTasksQueryParamsV4;
      }
    }
  } catch (error) {
    console.error(
      `Error loading task filters from session storage under key (${tabKey}):`,
      error
    );
  }

  return defaultValue;
}

/**
 * Resets task filters in tab-specific storage (sessionStorage)
 */
function _resetTabFilters(defaultParams: GetTasksQueryParamsV4): boolean {
  try {
    // Create deep copy to avoid reference issues
    const params = JSON.parse(JSON.stringify(defaultParams));

    sessionStorage.setItem(tabKey, JSON.stringify(params));

    return true;
  } catch (error) {
    console.error(
      `Error resetting task filters from session storage under key (${tabKey}):`,
      error
    );
    return false;
  }
}

/**
 * Utility to clean up filter storage when no longer needed
 */
function _cleanupTaskFilters(): void {
  try {
    sessionStorage.removeItem(tabKey);
  } catch (error) {
    console.error(
      `Error cleaning up task filters from session storage under key (${tabKey}):`,
      error
    );
  }
}
